import { Stack, Typography, TextField, Box, Button, CircularProgress } from "@mui/material";

import React, { useEffect, useRef, useState } from 'react';
import fetchLogo from '../Assets/cavvy-flag-tilt-nobackground.png';

import { applyUpdate, isUpdateAvailable } from "../utils/serviceWorkerRegistration";
import { Update } from "@mui/icons-material";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLSContext } from "../Context/useLSContext";
import { LSHeader } from "./LSHeader";
import { getCookie } from "../Context/CookieContext";
import { useDataContext } from "../Context/useDataContext";
import CreateApi from "../Settings/appSettings";
import { useAuth } from "react-oidc-context";
import { EventSelectionWindow } from "./EventSelectionWindow";
const boldFlagSrc = `${process.env.PUBLIC_URL}/Quilted Flag - Bold Zoom Tilt.png`;
const greenScriptLogo = `${process.env.PUBLIC_URL}/Stacked Cursive.png`;

// Min height content is designed for; scale down when available space is smaller
const MIN_CONTENT_HEIGHT = 520;

interface LiveScoringLoginProps {
    fetchScoringData: (scoreCodeParam: string, checkCookies: boolean) =>
        Promise<{ success: boolean; requiresGroupSelection?: boolean; eventID: string; scoreCode: string }>;
}

export const LiveScoringLogin = ({ fetchScoringData }: LiveScoringLoginProps) => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const api = CreateApi();
    const auth = useAuth();
    
    const { windowSize } = useLSContext();
    const { scoreCode, adminMode, userData, setUserData, adminEvents, setAdminEvents, setScoreCode } = useDataContext();
    const [eventSelectionOpen, setEventSelectionOpen] = useState(false);

    const [scoreCodeInput, setScoreCodeInput] = useState('');
    const [cookieScoreCode, setCookieScoreCode] = useState('');
    const [userRegistrations, setUserRegistrations] = useState([]);
    const [splashCache, setSplashCache] = useState<{ eventName: string; courseName: string; round: number } | null>(null);
    const [showGolfTooltip, setShowGolfTooltip] = useState(false);
    const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const [contentScale, setContentScale] = useState(1);

    const hasInput = scoreCodeInput.trim().length > 0;

    const signOutRedirect = () => {
        const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
        const logoutUri = window.location.origin;
        const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    };

    useEffect(() => {
        try {
            const cached = localStorage.getItem('momentGolf-ls-splashCache');
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed?.eventName || parsed?.courseName) {
                    setSplashCache(parsed);
                }
            }
        } catch {
            // non-fatal
        }
    }, []);

    // 5-second tooltip after user enters a code
    useEffect(() => {
        if (tooltipTimerRef.current) {
            clearTimeout(tooltipTimerRef.current);
            tooltipTimerRef.current = null;
        }
        if (hasInput) {
            tooltipTimerRef.current = setTimeout(() => {
                setShowGolfTooltip(true);
            }, 5000);
        } else {
            setShowGolfTooltip(false);
        }
        return () => {
            if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
        };
    }, [scoreCodeInput]);

    useEffect(() => {
        // console.log('Score Code:', scoreCode);
        const fetchData = async (cookieScoreCode: string) => {
            const result = await fetchScoringData(cookieScoreCode, false);
            if (result.success) {
                if (adminMode || scoreCode.includes('-admin')) {
                    navigate(`/leaderboard/?eventID=${result.eventID}`);
                } else {
                    navigate(`/live-scoring/?event=${result.eventID}&scoreCode=${result.scoreCode}`);
                }
            } else {
                setCookieScoreCode('');
            }
        }

        const cookieScoreCode = getCookie('momentGolf-ls-current-scoreCode');
        // console.log('Cookie Score Code:', cookieScoreCode);
        
        if (cookieScoreCode || scoreCode) {
            setCookieScoreCode(cookieScoreCode || scoreCode);
            fetchData(cookieScoreCode || scoreCode);
        }
        
    }, [scoreCode]);

    // Auto-submit when a scoreCode URL param is present (e.g. from a QR code scan)
    useEffect(() => {
        const urlScoreCode = searchParams.get('scoreCode');
        const urlEventId   = searchParams.get('eventId') ?? undefined;
        if (!urlScoreCode) return;
        const existingCookie = getCookie('momentGolf-ls-current-scoreCode');
        if (existingCookie || scoreCode) return; // already handling a session
        executeSearch(urlScoreCode, urlEventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchRegistrations = async () => {
            // console.log('Fetching registrations for user:', auth.user?.profile?.sub);
            const registrations = await api.get('/ls/GetUserRegistrations', {
                params: { userID: auth.user?.profile?.sub }
            });
            // console.log('Registrations:', registrations.data);
            setUserRegistrations(registrations.data);

            const userData = await api.get('/ls/GetUser', {
                params: { userID: auth.user?.profile?.sub }
            });
            // console.log('User Data:', userData.data);
            setUserData(userData.data);

            if (userData.data && userData.data.accountTier > 2) {
                const adminEvents = await api.get('/ls/GetAdminEvents', {
                    params: { userID: auth.user?.profile?.sub }
                });
                // console.log('Admin Events:', adminEvents.data);
                setAdminEvents(adminEvents.data);
            }
        };
  
        const checkScoreCode = getCookie('momentGolf-ls-current-scoreCode');
        if (auth.user && auth.isAuthenticated && !auth.isLoading && !checkScoreCode) {
            // console.log('User is authenticated and no score code, fetching registrations');
            fetchRegistrations();
        }
    }, [auth]);

    // Scale content to fit when height is constrained
    useEffect(() => {
        const el = contentContainerRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            const { height } = entries[0]?.contentRect ?? { height: 0 };
            const scale = height > 0 ? Math.min(1, height / MIN_CONTENT_HEIGHT) : 1;
            setContentScale(scale);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const executeSearch = async (code: string, eventId?: string) => {
        const result = await fetchScoringData(code, false, eventId);
        if (result.success) {
            navigate(`/live-scoring/?event=${result.eventID}&scoreCode=${result.scoreCode}`);
        } else if (!result.requiresGroupSelection) {
            alert('Data was not fetched. Please try the score code again.');
        }
        // requiresGroupSelection: FirstTeeDrawer opens globally in App — no alert needed
    };

    const handleSearch = async () => {
        if (!scoreCodeInput.trim()) return;
        await executeSearch(scoreCodeInput);
    };

    // Circle sizing
    const CIRCLE_SIZE = 120;
    const BORDER_WIDTH = 6;
    const OUTER_SIZE = CIRCLE_SIZE + BORDER_WIDTH * 2;

    return (
        <Stack id='scoring-login' direction='column' 
            sx={{
                width: '100dvw', 
                height: '100dvh',
                backgroundColor: 'sepia.background',
                overscrollBehavior: 'none',
                overflow: 'hidden',
                position: 'fixed',
                top: 0,
                left: 0,
                alignItems: 'center',
                justifyContent: 'center'
            }}>
            <LSHeader />
            <Stack direction='column' width='90%' height='100%' sx={{
                marginTop: `${windowSize.appBarHeight + 10}px`,
                marginBottom: `${windowSize.appBarHeight + 20}px`,
                transform: `scale(${windowSize.height < 700 ? windowSize.height / 800 : 1})`,
            }}>

                {/* Loading overlay */}
                {(cookieScoreCode || scoreCode) &&
                    <Stack direction='column' spacing={2} sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                    }}>
                        <img src={fetchLogo} width="200" height="max-content" style={{ marginBottom: 10 }} />
                        {splashCache?.eventName && (
                            <Typography variant='h6' sx={{ color: 'white', fontWeight: 600, textAlign: 'center' }}>
                                {splashCache.eventName}
                            </Typography>
                        )}
                        {splashCache?.courseName && (
                            <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>
                                {splashCache.courseName}{splashCache.round > 1 ? ` · Round ${splashCache.round}` : ''}
                            </Typography>
                        )}
                        <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>Fetching your data...</Typography>
                        <CircularProgress sx={{ color: 'white' }} />
                    </Stack>
                }

                {/* Update prompt */}
                {isUpdateAvailable() && (
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={applyUpdate}
                            startIcon={<Update />}
                        >
                            Update Before Continuing
                        </Button>
                    </Box>
                )}

                {/* Event selection drawer */}
                {eventSelectionOpen && (() => {
                    if (adminMode) {
                        const normalizedAdminEvents = (adminEvents ?? []).map((event: any) => ({
                            _id: event._id,
                            scoreCode: event.scoreCode,
                            eventScoreID: event._id,
                            teeTimes: [{ teeTime: event.startDate }],
                            eventDetails: {
                                startDate: event.startDate,
                                eventName: event.eventName,
                                courseName: event.courseName,
                            },
                        }));
                        return (
                            <EventSelectionWindow
                                open={eventSelectionOpen}
                                setOpen={setEventSelectionOpen}
                                registrations={normalizedAdminEvents}
                                title="Pick a Leaderboard"
                                onSelect={(reg) => {
                                    setScoreCodeInput(reg.scoreCode);
                                    setTimeout(() => {
                                        setScoreCode(`${reg.scoreCode}-admin`);
                                        navigate(`/leaderboard/?eventID=${reg._id}`);
                                    }, 1000);
                                }}
                            />
                        );
                    }
                    return (
                        <EventSelectionWindow
                            open={eventSelectionOpen}
                            setOpen={setEventSelectionOpen}
                            registrations={userRegistrations}
                            onSelect={(reg) => {
                                setScoreCodeInput(reg.scoreCode);
                                setTimeout(() => executeSearch(reg.scoreCode), 1000);
                            }}
                        />
                    );
                })()}

                {/* Bottom shape: rectangle + circle */}
                {!cookieScoreCode && !scoreCode && !isUpdateAvailable() && (
                    <>
                    {/* Rectangle card */}
                    <Stack direction='column' spacing={2} 
                    
                        sx={{
                            //alignSelf: 'stretch',
                            flex: 1,
                            backgroundColor: 'sepia.background',
                            borderRadius: 4,
                            px: 3,
                            pt: 4,
                            pb: 10,
                            height: `calc(100% - ${OUTER_SIZE + 20}px - env(safe-area-inset-bottom))`,
                            //display: 'flex',
                            //flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            position: 'relative',
                            border: '3px solid',
                            borderColor: 'sepia.dark',
                            boxShadow: 3
                        }}>
                            <Stack direction='column' spacing={3}
                                sx={{ flex: 1, alignItems: 'center', 
                                justifyContent: 'center',}}>
                                
                                <img src={greenScriptLogo} alt="Moment Golf" style={{ width: '70%' }} />
                                <Typography variant='body1' sx={{ color: 'primary.main', fontWeight: 400 }}>
                                    Start by signing in to select one of your events.
                                </Typography>
                                <Stack direction='row' spacing={2} sx={{ width: '100%', maxWidth: 340, justifyContent: 'center' }}>
                                    {auth?.user
                                        ? <Button variant='contained' color='primary' size='large' sx={{ flex: 1 }}
                                            onClick={async () => {
                                                auth.signoutSilent();
                                                signOutRedirect();
                                            }}>
                                            <Typography variant='caption'>Sign Out</Typography>
                                        </Button>
                                        : <Button variant='contained' color='primary' size='large' sx={{ flex: 1 }}
                                            onClick={async () => {
                                                localStorage.setItem('redirectURL', window.location.pathname);
                                                auth.signinRedirect();
                                            }}>
                                            <Typography variant='caption'>Sign In</Typography>
                                        </Button>
                                    }
                                    {((userRegistrations && userRegistrations.length > 0) || (adminEvents && adminEvents.length > 0)) &&
                                        <Button variant='contained' color='primary' size='large' sx={{ flex: 1 }}
                                            onClick={() => setEventSelectionOpen(true)}>
                                            <Typography variant='caption'>Select Event</Typography>
                                        </Button>
                                    }
                                </Stack>
                            </Stack>
                            

                            <Typography variant='body1' sx={{ color: 'primary.main', fontWeight: 400 }}>
                                Or enter a score code to start scoring:
                            </Typography>

                            <TextField
                                label="Score Code"
                                value={scoreCodeInput}
                                onChange={(e) => setScoreCodeInput(e.target.value.toUpperCase())}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                                variant="outlined"
                                sx={{ width: '100%', maxWidth: 340 }}
                            />
                            {/* Circle search button */}
                            <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                transform: `translate(-50%, 50%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 5,
                            }}>
                                {/* "Let's golf" box on top of circle center */}
                                {/* {showGolfTooltip && (
                                    <Box sx={{
                                        position: 'absolute',
                                        left: '50%',
                                        top: '50%',
                                        transform: 'translate(-50%, -20px)',
                                        zIndex: 10,
                                        px: 1,
                                        py: 1,
                                        width: 70,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(62, 60, 60, 0.8)',
                                        color: 'primary.contrastText',
                                        boxShadow: 3,
                                    }}>
                                        <Typography variant='body2' sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                            Let's Golf
                                        </Typography>
                                    </Box>
                                )} */}
                                {/* Circle container */}
                                <Box sx={{
                                    width: OUTER_SIZE,
                                    height: OUTER_SIZE,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: hasInput ? 'pointer' : 'default',
                                    transition: 'box-shadow 0.4s ease',
                                }} onClick={handleSearch}>
                                    {/* Spinning border — gradient ring behind, only the border appears to spin */}
                                    {hasInput && (
                                        <Box sx={{
                                            position: 'absolute',
                                            width: OUTER_SIZE,
                                            height: OUTER_SIZE,
                                            borderRadius: '50%',
                                            background: 'conic-gradient(#8B6914, #CD7F32, #E8A83E, #CD7F32, #CD7F32)',
                                            boxShadow: '0px 0px 18px 4px rgba(205, 127, 50, 0.6)',
                                            animation: 'rotateBronze 1.8s linear infinite',
                                            '@keyframes rotateBronze': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' },
                                            },
                                        }} />
                                    )}
                                    {/* Inner circle — static, masks center of gradient to create border effect */}
                                    <Box sx={{
                                        position: 'absolute',
                                        width: CIRCLE_SIZE+2,
                                        height: CIRCLE_SIZE+2,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                    }}>
                                        <img
                                            src={boldFlagSrc}
                                            alt="Search"
                                            style={{ width: '85%', height: '85%', objectFit: 'contain' }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                        <Stack direction='row' spacing={2}
                        sx={{ height: `${OUTER_SIZE/2 + 20}px`, alignItems: 'flex-end', justifyContent: 'center' }}>
                            <Typography variant='subtitle2' sx={{ color: 'primary.main', fontWeight: 500 }}>
                                For Every Moment. For Every Golfer.<sup style={{ fontSize: '0.65em', verticalAlign: 'super' }}>TM</sup>
                            </Typography>
                        </Stack>
                    </>
                )}
            
            </Stack>
            
        </Stack>
    );
}
